import React, { useEffect, useState, useCallback, useMemo, memo, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Box,
  Button,
  Card, CardContent, Checkbox, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Grid, IconButton, InputAdornment, InputBase, ListSubheader, MenuItem, Select, TextField, Tooltip, Typography,
  Autocomplete, Switch, Slider, Popover,
} from '@mui/material';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import StrictModeDroppable from './components/StrictModeDroppable';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import AccountCircle from '@mui/icons-material/AccountCircle';
import GetAppIcon from '@mui/icons-material/GetApp';
import { GripVertical, User, Play, Trash2, FolderKanban, UserCheck, Sparkles, Repeat, SlidersHorizontal, Clock, RotateCcw, Search, X } from 'lucide-react';
import ThumbnailZoom from '../common/ThumbnailZoom';
import PlaylistManagerDialog from '../PlaylistManagerDialog';
import ImportPlaylistDialog from '../ImportPlaylistDialog';
import { formatExportFilename, createExportPayload, parseImportPayload, saveActivePlaylistSnapshot, resolveNextTrack, parseTimeStringToSeconds, formatSecondsToTime } from '../../utils/playlistUtils';
import { useTranslation } from 'react-i18next';

const PlayListCard = ({
  playlist,
  videoPlayNow,
  setPlaylist,
  currentPlaylist,
  setCurrentPlaylist,
  myPlaylists = [],
  setMyPlaylists,
  onCreatePlaylist,
  onRenamePlaylist,
  onDeletePlaylist,
  userPresetPlaylists,
  originPresetPlaylists,
  setOriginPresetPlaylists,
  originPresetPlaylist,
  setOriginPresetPlaylist,
  userPresetPlaylist,
  setUserPresetPlaylist,
  setCurrentPlaylistItem,
  currentPlaylistItem,
  videoId,
  isIntegratedScreen = false,
  continuousPlay,
  onToggleContinuousPlay,
  crossfadeEnabled,
  onToggleCrossfade,
  crossfadeDuration,
  onUpdateCrossfadeDuration,
}) => {
  const { t } = useTranslation();
  const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);
  const [deletionItemId, setDeletionItemId] = useState(null);
  const [assignedToDialogOpen, setAssignedToDialogOpen] = useState(false);
  const [assignedToItemId, setAssignedToItemId] = useState(null);
  const [assignedToInputValue, setAssignedToInputValue] = useState('');
  const [startTimeDialogOpen, setStartTimeDialogOpen] = useState(false);
  const [startTimeItemId, setStartTimeItemId] = useState(null);
  const [startTimeInputValue, setStartTimeInputValue] = useState('');
  const [endTimeInputValue, setEndTimeInputValue] = useState('');
  const [playlistObj, setPlaylistObj] = useState(playlist);
  const [crossfadeAnchorEl, setCrossfadeAnchorEl] = useState(null);
  const [playbackProgress, setPlaybackProgress] = useState({});
  const crossfadeInProgressRef = useRef(false);

  // Escuchar eventos de progreso para actualizar el indicador circular alrededor de Play y el track activo
  useEffect(() => {
    const handleProgress = (e) => {
      const { videoId: progVidId, progress } = e.detail || {};
      if (progVidId) {
        if (progress !== undefined) {
          setPlaybackProgress(prev => ({
            ...prev,
            [progVidId]: progress,
          }));
        }
        if (!crossfadeInProgressRef.current) {
          setActivePlayingVideoId(progVidId);
        }
      }
    };

    window.addEventListener('garaoke_playback_progress', handleProgress);

    let bc = null;
    try {
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        bc = new BroadcastChannel('garaoke_player_sync');
        bc.onmessage = (event) => {
          if (event.data?.type === 'playbackProgress') {
            const { videoId: progVidId, progress } = event.data;
            if (progVidId) {
              if (progress !== undefined) {
                setPlaybackProgress(prev => ({
                  ...prev,
                  [progVidId]: progress,
                }));
              }
              if (!crossfadeInProgressRef.current) {
                setActivePlayingVideoId(progVidId);
              }
            }
          }
        };
      }
    } catch (e) {}

    return () => {
      window.removeEventListener('garaoke_playback_progress', handleProgress);
      if (bc) bc.close();
    };
  }, []);

  const getTrackDuration = useCallback((itemObj) => {
    if (itemObj?.duration) return itemObj.duration;
    const vId = itemObj?.videoId;
    if (vId) {
      try {
        const cached = JSON.parse(localStorage.getItem('garaoke_duration_cache') || '{}');
        if (cached[vId]) return cached[vId];
      } catch (e) {}
    }
    return null;
  }, []);

  // Estados locales de respaldo por si no se reciben props
  const [localContinuous, setLocalContinuous] = useState(() => {
    return localStorage.getItem('garaoke_continuous_play') === 'true';
  });
  const [localCrossfade, setLocalCrossfade] = useState(() => {
    return localStorage.getItem('garaoke_crossfade_enabled') === 'true';
  });
  const [localCrossfadeSec, setLocalCrossfadeSec] = useState(() => {
    const v = Number(localStorage.getItem('garaoke_crossfade_duration'));
    return !isNaN(v) && v >= 2 && v <= 15 ? v : (Number(import.meta.env?.VITE_DEFAULT_CROSSFADE_SEC) || 5);
  });

  const isContinuous = continuousPlay !== undefined ? continuousPlay : localContinuous;
  const isCrossfade = crossfadeEnabled !== undefined ? crossfadeEnabled : localCrossfade;
  const currentCrossfadeSec = crossfadeDuration !== undefined ? crossfadeDuration : localCrossfadeSec;

  const handleLocalToggleContinuous = useCallback((val) => {
    const nextVal = typeof val === 'boolean' ? val : !localContinuous;
    setLocalContinuous(nextVal);
    try { localStorage.setItem('garaoke_continuous_play', String(nextVal)); } catch (e) {}
  }, [localContinuous]);

  const handleLocalToggleCrossfade = useCallback((val) => {
    const nextVal = typeof val === 'boolean' ? val : !localCrossfade;
    setLocalCrossfade(nextVal);
    try { localStorage.setItem('garaoke_crossfade_enabled', String(nextVal)); } catch (e) {}
  }, [localCrossfade]);

  const handleLocalUpdateCrossfadeSec = useCallback((val) => {
    const nextVal = Math.max(2, Math.min(15, Number(val) || 5));
    setLocalCrossfadeSec(nextVal);
    try { localStorage.setItem('garaoke_crossfade_duration', String(nextVal)); } catch (e) {}
  }, []);

  // Recopilar todos los participantes asignados en todas las listas disponibles para el predictivo
  const existingParticipants = useMemo(() => {
    const participantsSet = new Set();

    const scanItems = (items) => {
      if (!Array.isArray(items)) return;
      items.forEach((item) => {
        if (item && item.assignedTo && typeof item.assignedTo === 'string') {
          const trimmed = item.assignedTo.trim();
          if (trimmed) {
            participantsSet.add(trimmed);
          }
        }
      });
    };

    if (Array.isArray(myPlaylists)) {
      myPlaylists.forEach((pl) => scanItems(pl?.items));
    }
    scanItems(playlist);
    scanItems(playlistObj);
    if (Array.isArray(userPresetPlaylists)) {
      userPresetPlaylists.forEach((pl) => scanItems(pl?.items));
    }

    return Array.from(participantsSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [myPlaylists, playlist, playlistObj, userPresetPlaylists]);

  // Estados para diálogos de gestión, importación y vaciado
  const [playlistManagerOpen, setPlaylistManagerOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [pendingImportData, setPendingImportData] = useState(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  // Estados para buscador dinámico del lado del cliente
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);

  const filteredPlaylist = useMemo(() => {
    if (!Array.isArray(playlistObj)) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return playlistObj;
    return playlistObj.filter(item => {
      const title = (item?.title || item?.item?.title || '').toLowerCase();
      const assigned = (item?.assignedTo || item?.item?.assignedTo || item?.singer || item?.performer || '').toLowerCase();
      return title.includes(q) || assigned.includes(q);
    });
  }, [playlistObj, searchQuery]);

  // Detectar si la lista actual es una de las listas propias del usuario
  const isCurrentMyPlaylist = useMemo(() => {
    return myPlaylists.some(pl => pl.id === currentPlaylist);
  }, [myPlaylists, currentPlaylist]);

  const currentMyPlaylistObj = useMemo(() => {
    return myPlaylists.find(pl => pl.id === currentPlaylist || pl.name === currentPlaylist);
  }, [myPlaylists, currentPlaylist]);

  const currentPlaylistDisplayName = currentMyPlaylistObj ? currentMyPlaylistObj.name : currentPlaylist;

  const allAvailableValues = useMemo(() => {
    const vals = [];
    if (Array.isArray(myPlaylists)) myPlaylists.forEach(p => { if (p.id) vals.push(p.id); });
    if (Array.isArray(userPresetPlaylists)) userPresetPlaylists.forEach(p => { if (p.name) vals.push(p.name); });
    if (Array.isArray(originPresetPlaylists)) originPresetPlaylists.forEach(p => { if (p.name) vals.push(p.name); });
    return vals;
  }, [myPlaylists, userPresetPlaylists, originPresetPlaylists]);

  const presetsLoaded = originPresetPlaylists && originPresetPlaylists.length > 0;

  const safeSelectValue = useMemo(() => {
    if (!currentPlaylist) return allAvailableValues[0] || '';
    if (allAvailableValues.includes(currentPlaylist)) return currentPlaylist;
    // Si aún no terminaron de cargar las listas o ya tiene un currentPlaylist asignado, respetarlo
    return currentPlaylist;
  }, [allAvailableValues, currentPlaylist]);

  const handleConfirmClear = useCallback(() => {
    setPlaylist([]);
    setPlaylistObj([]);
    setClearDialogOpen(false);
  }, [setPlaylist]);

  /* eslint-disable */
  const loadPlaylists = useCallback(async () => {
    try {
      const files = import.meta.glob('../../playlists/*.{json,txt}', { query: '?raw', import: 'default', eager: true });
      const playlists = Object.entries(files).map(([filepath, content]) => {
        const filename = filepath.split('/').pop().replace(/\.(json|txt)$/i, '');
        try {
          const raw = typeof content === 'string' ? content : JSON.stringify(content);
          const parsed = parseImportPayload(raw, filename);

          let plName = filename;
          try {
            const directObj = typeof content === 'string' ? JSON.parse(content) : content;
            if (directObj && typeof directObj.name === 'string' && directObj.name.trim()) {
              plName = directObj.name.trim();
            } else if (parsed && parsed.name) {
              plName = parsed.name;
            }
          } catch {
            if (parsed && parsed.name) plName = parsed.name;
          }

          return {
            name: plName,
            items: parsed.items || []
          };
        } catch (err) {
          console.warn('Error loading preset playlist file:', filepath, err);
          return { name: filename, items: [] };
        }
      });
      setOriginPresetPlaylists(playlists);
    } catch (err) {
      console.warn('Error loading preset playlists:', err);
    }
  }, [setOriginPresetPlaylists]);

  useEffect(() => {
    loadPlaylists();
  }, [loadPlaylists]);


  const getOriginPresetPlayListByName = useCallback((name) => {
    return originPresetPlaylists.find(pl => pl.name === name)?.items || [];
  }, [originPresetPlaylists]);

  const getUserPresetPlayListByName = useCallback((name) => {
    return userPresetPlaylists.find(pl => pl.name === name)?.items || [];
  }, [userPresetPlaylists]);

  useEffect(() => {
    const myPl = Array.isArray(myPlaylists) && myPlaylists.find(pl => pl.id === currentPlaylist || pl.name === currentPlaylist);
    if (myPl && myPl.items && myPl.items.length > 0) {
      setPlaylistObj(myPl.items);
    } else {
      const oppl = getOriginPresetPlayListByName(currentPlaylist);
      const uppl = getUserPresetPlayListByName(currentPlaylist);
      const activeItems = (uppl && uppl.length > 0) ? uppl : oppl;
      if (activeItems && activeItems.length > 0) {
        setPlaylistObj(activeItems);
      } else if (Array.isArray(playlist) && playlist.length > 0) {
        setPlaylistObj(playlist);
      } else {
        setPlaylistObj(activeItems || []);
      }
    }
  }, [currentPlaylist, myPlaylists, userPresetPlaylists, originPresetPlaylists, getOriginPresetPlayListByName, getUserPresetPlayListByName, playlist]);

  // Si la playlist cambia como prop desde el anfitrión o Firestore, actualizar inmediatamente el estado visual
  useEffect(() => {
    if (Array.isArray(playlist) && playlist.length > 0) {
      setPlaylistObj(playlist);
    }
  }, [playlist]);

  const updateActivePlaylistItems = useCallback((newItems) => {
    setPlaylistObj(newItems);
    setPlaylist(newItems);
    if (typeof setMyPlaylists === 'function') {
      setMyPlaylists(prev => {
        if (!Array.isArray(prev)) return prev;
        const updated = prev.map(pl => {
          if (pl.id === currentPlaylist || pl.name === currentPlaylist) {
            return { ...pl, items: newItems };
          }
          return pl;
        });
        try {
          localStorage.setItem('garaoke_my_playlists', JSON.stringify(updated));
        } catch (e) {}
        return updated;
      });
    }
    try {
      localStorage.setItem('garaoke_user_playlist', JSON.stringify(newItems));
    } catch (e) {}
  }, [setPlaylist, setMyPlaylists, currentPlaylist]);

  const handleDragEndPlaylist = useCallback((result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    const newPlaylist = Array.from(playlistObj);
    const [reorderedItem] = newPlaylist.splice(result.source.index, 1);
    newPlaylist.splice(result.destination.index, 0, reorderedItem);
    updateActivePlaylistItems(newPlaylist);
  }, [playlistObj, updateActivePlaylistItems]);

  const handleCheckVideo = useCallback((checked, itemId) => {
    const updatedPlaylist = playlistObj.map(item => {
      if (item.id === itemId) {
        return { ...item, played: checked };
      }
      return item;
    });
    updateActivePlaylistItems(updatedPlaylist);
  }, [playlistObj, updateActivePlaylistItems]);

  const playedCount = useMemo(() => {
    return Array.isArray(playlistObj) ? playlistObj.filter(item => Boolean(item.played)).length : 0;
  }, [playlistObj]);

  const handleUncheckAllPlayed = useCallback(() => {
    if (!Array.isArray(playlistObj) || playlistObj.length === 0) return;
    const hasAnyPlayed = playlistObj.some(item => Boolean(item.played));
    if (!hasAnyPlayed) return;

    const updatedPlaylist = playlistObj.map(item => ({
      ...item,
      played: false
    }));
    updateActivePlaylistItems(updatedPlaylist);
  }, [playlistObj, updateActivePlaylistItems]);

  const handleDeleteAssignedTo = useCallback((itemId) => {
    const updatedPlaylist = playlistObj.map(item => {
      if (item.id === itemId) {
        return { ...item, assignedTo: '' };
      }
      return item;
    });
    updateActivePlaylistItems(updatedPlaylist);
  }, [playlistObj, updateActivePlaylistItems]);

  const [activePlayingVideoId, setActivePlayingVideoId] = useState(videoId);

  useEffect(() => {
    setActivePlayingVideoId(videoId || null);
  }, [videoId]);

  useEffect(() => {
    if (currentPlaylistItem?.item?.videoId) {
      setActivePlayingVideoId(currentPlaylistItem.item.videoId);
    } else if (!currentPlaylistItem || !currentPlaylistItem.item) {
      setActivePlayingVideoId(null);
    }
  }, [currentPlaylistItem]);

  // Escuchar eventos de cambio de pista desde el reproductor en tiempo real
  useEffect(() => {
    const handlePlayerSync = (event) => {
      const data = event?.data;
      if (!data) return;

      if (data.type === 'playerClosed' || data.type === 'screenClosed' || data.type === 'stopVideo' || (data.type === 'changeVideo' && !data.videoId)) {
        crossfadeInProgressRef.current = false;
        setActivePlayingVideoId(null);
        return;
      }

      if (data.type === 'START_CROSSFADE' || data.type === 'triggerCrossfade') {
        crossfadeInProgressRef.current = true;
        return;
      }

      if (data.type === 'onDeckChanged') {
        crossfadeInProgressRef.current = false;
        if (data.videoId) {
          setActivePlayingVideoId(data.videoId);
        }
        return;
      }

      if (data.type === 'changeVideo' || data.type === 'onMidpointCrossfade' || data.type === 'videoPlaying') {
        if (data.type === 'changeVideo') {
          crossfadeInProgressRef.current = false;
        }
        if (data.videoId) {
          setActivePlayingVideoId(data.videoId);

          // Si finalizó una pista anterior, marcarla como reproducida automáticamente
          const prevVid = data.previousVideoId || (activePlayingVideoId && activePlayingVideoId !== data.videoId ? activePlayingVideoId : null);
          if (prevVid) {
            setPlaylistObj(prevList => {
              if (!Array.isArray(prevList)) return prevList;
              let changed = false;
              const updated = prevList.map(it => {
                if ((it.videoId === prevVid || it.id === prevVid) && !it.played) {
                  changed = true;
                  return { ...it, played: true };
                }
                return it;
              });
              if (changed) {
                updateActivePlaylistItems(updated);
                return updated;
              }
              return prevList;
            });
          }
        }
      }
    };

    let channel = null;
    try {
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        channel = new BroadcastChannel('garaoke_player_sync');
        channel.onmessage = handlePlayerSync;
      }
    } catch (e) {}

    window.addEventListener('message', handlePlayerSync);
    return () => {
      window.removeEventListener('message', handlePlayerSync);
      if (channel) channel.close();
    };
  }, [activePlayingVideoId, updateActivePlaylistItems]);

  // Sincronizar el snapshot de la lista activa en pantalla para que el loader y Player lo consulten bajo demanda
  useEffect(() => {
    if (Array.isArray(playlistObj)) {
      saveActivePlaylistSnapshot(currentPlaylist, playlistObj);
    }
  }, [currentPlaylist, playlistObj]);

  // Mantener al reproductor (Player) enterado en tiempo real de la siguiente pista según la consulta inteligente
  useEffect(() => {
    if (!isContinuous && !isCrossfade) return;
    if (!Array.isArray(playlistObj) || playlistObj.length === 0) return;

    const currentVid = activePlayingVideoId || videoId;
    const { nextSong, isEndOfPlaylist } = resolveNextTrack(playlistObj, currentVid, currentPlaylistItem, currentPlaylist);

    const payload = {
      type: 'setNextSong',
      nextSong: nextSong || null,
      isEndOfPlaylist: isEndOfPlaylist
    };

    try {
      if (typeof window !== 'undefined') {
        window.postMessage(payload, '*');
      }
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        const bc = new BroadcastChannel('garaoke_player_sync');
        bc.postMessage(payload);
        bc.close();
      }
    } catch (e) {}
  }, [playlistObj, videoId, activePlayingVideoId, isContinuous, isCrossfade, currentPlaylist, currentPlaylistItem]);

  const handleOpenDeletionDialog = useCallback((itemId) => {
    setDeletionItemId(itemId);
    setDeletionDialogOpen(true);
  }, []);

  const handleOpenEditAssignedToDialog = useCallback((itemId) => {
    const currentItem = playlistObj.find(item => item.id === itemId);
    setAssignedToItemId(itemId);
    setAssignedToInputValue(currentItem?.assignedTo || '');
    setAssignedToDialogOpen(true);
  }, [playlistObj]);

  const handleCloseDeletionDialog = useCallback(() => {
    setDeletionItemId(null);
    setDeletionDialogOpen(false);
  }, []);

  const handleCloseAssignedToDialog = useCallback(() => {
    setAssignedToItemId(null);
    setAssignedToInputValue('');
    setAssignedToDialogOpen(false);
  }, []);

  const handleConfirmDeletion = useCallback(() => {
    const newPlaylist = playlistObj.filter(item => item.id !== deletionItemId);
    updateActivePlaylistItems(newPlaylist);
    handleCloseDeletionDialog();
  }, [deletionItemId, playlistObj, updateActivePlaylistItems, handleCloseDeletionDialog]);

  const handleConfirmAssignedTo = useCallback((nameToAssign) => {
    const finalName = typeof nameToAssign === 'string' ? nameToAssign.trim() : assignedToInputValue.trim();
    const updatedPlaylist = playlistObj.map(item => {
      if (item.id === assignedToItemId) {
        return { ...item, assignedTo: finalName };
      }
      return item;
    });
    updateActivePlaylistItems(updatedPlaylist);
    handleCloseAssignedToDialog();
  }, [assignedToItemId, assignedToInputValue, playlistObj, updateActivePlaylistItems, handleCloseAssignedToDialog]);

  const handleOpenEditStartTimeDialog = useCallback((itemId) => {
    const currentItem = playlistObj.find(item => item.id === itemId);
    setStartTimeItemId(itemId);
    const existingStartSec = currentItem?.startTime ?? currentItem?.t ?? 0;
    const existingEndSec = currentItem?.endTime ?? 0;
    setStartTimeInputValue(existingStartSec > 0 ? formatSecondsToTime(existingStartSec) : '');
    setEndTimeInputValue(existingEndSec > 0 ? formatSecondsToTime(existingEndSec) : '');
    setStartTimeDialogOpen(true);
  }, [playlistObj]);

  const handleCloseStartTimeDialog = useCallback(() => {
    setStartTimeItemId(null);
    setStartTimeInputValue('');
    setEndTimeInputValue('');
    setStartTimeDialogOpen(false);
  }, []);

  const handleConfirmStartTime = useCallback((explicitStartSec = null, explicitEndSec = null) => {
    let finalStartSec = 0;
    if (explicitStartSec !== null && !isNaN(Number(explicitStartSec))) {
      finalStartSec = Math.max(0, Number(explicitStartSec));
    } else {
      finalStartSec = parseTimeStringToSeconds(startTimeInputValue);
    }

    let finalEndSec = 0;
    if (explicitEndSec !== null && !isNaN(Number(explicitEndSec))) {
      finalEndSec = Math.max(0, Number(explicitEndSec));
    } else {
      finalEndSec = parseTimeStringToSeconds(endTimeInputValue);
    }

    // Validación de seguridad: si endTime > 0 y <= startTime, alertar o ajustar
    if (finalEndSec > 0 && finalEndSec <= finalStartSec) {
      alert('El tiempo de finalización debe ser mayor que el tiempo de inicio.');
      return;
    }

    const updatedPlaylist = playlistObj.map(item => {
      if (item.id === startTimeItemId) {
        return {
          ...item,
          startTime: finalStartSec,
          endTime: finalEndSec,
        };
      }
      return item;
    });

    updateActivePlaylistItems(updatedPlaylist);
    handleCloseStartTimeDialog();
  }, [startTimeItemId, startTimeInputValue, endTimeInputValue, playlistObj, updateActivePlaylistItems, handleCloseStartTimeDialog]);

  // Exportar lista con formato garaoke-[AAAA-MM-dd]_[nombre_de_lista].txt
  const handleExport = useCallback(() => {
    const playlistName = currentMyPlaylistObj ? currentMyPlaylistObj.name : currentPlaylist;
    const payload = createExportPayload(playlistName, playlistObj);
    const filename = formatExportFilename(playlistName);
    const text = JSON.stringify(payload, null, 2);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }, [currentMyPlaylistObj, currentPlaylist, playlistObj]);

  // Seleccionar archivo .txt para importar y abrir diálogo de opciones
  const handleImportFileSelect = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt,application/json,text/plain';
    input.onchange = (event) => {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const parsed = parseImportPayload(text, file.name);
          if (!parsed || !Array.isArray(parsed.items)) {
            alert('El archivo seleccionado no tiene un formato válido de lista de canciones.');
            return;
          }
          setPendingImportData(parsed);
          setImportDialogOpen(true);
        } catch (err) {
          console.warn('Error al procesar archivo de lista:', err);
          alert(err.message || 'Error al leer el archivo. Asegúrate de que sea un archivo de texto o JSON válido.');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }, []);

  // Acciones del diálogo de importación
  const handleConfirmImportOverwrite = useCallback((items) => {
    setPlaylist(items);
    setPlaylistObj(items);
    setImportDialogOpen(false);
    setPendingImportData(null);
  }, [setPlaylist]);

  const handleConfirmImportAppend = useCallback((itemsToAppend) => {
    const currentItems = playlistObj || [];
    const maxId = currentItems.reduce((max, it) => Math.max(max, typeof it.id === 'number' ? it.id : 0), 0);
    const remapped = itemsToAppend.map((it, idx) => ({
      ...it,
      id: maxId + idx + 1,
      played: false
    }));
    const combined = [...currentItems, ...remapped];
    setPlaylist(combined);
    setPlaylistObj(combined);
    setImportDialogOpen(false);
    setPendingImportData(null);
  }, [playlistObj, setPlaylist]);

  const handleConfirmImportCreateNew = useCallback((newName, items) => {
    if (onCreatePlaylist) {
      onCreatePlaylist(newName, items);
    }
    setImportDialogOpen(false);
    setPendingImportData(null);
  }, [onCreatePlaylist]);

  const handleChangePlaylist = useCallback((e) => {
    const selected = e.target.value;
    setCurrentPlaylist(selected);
    try {
      localStorage.setItem('garaoke_current_playlist', selected);
    } catch (err) {}
    const myPl = myPlaylists.find(pl => pl.id === selected);
    if (myPl) {
      setPlaylistObj(myPl.items || []);
    } else {
      const oppl = getOriginPresetPlayListByName(selected);
      const uppl = getUserPresetPlayListByName(selected);
      const activeItems = (uppl && uppl.length > 0) ? uppl : oppl;
      setPlaylistObj(activeItems);
    }
  }, [myPlaylists, setCurrentPlaylist, getOriginPresetPlayListByName, getUserPresetPlayListByName]);

  /* eslint-enable */

  const playNow = (item) => {
    setActivePlayingVideoId(item.videoId);
    setCurrentPlaylistItem({ playlist: currentPlaylist, item: item });
    const itemStartTime = Math.max(0, Number(item.startTime ?? item.t ?? 0) || 0);
    const itemEndTime = Math.max(0, Number(item.endTime ?? 0) || 0);
    videoPlayNow(item.videoId, false, true, null, itemStartTime, itemEndTime, item);
  };
  const deletionDialog = (
    <Dialog
      open={deletionDialogOpen}
      onClose={handleCloseDeletionDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        sx: {
          background: '#0f081d !important',
          backgroundColor: '#0f081d !important',
          backdropFilter: 'none !important',
          border: '1px solid rgba(255, 45, 155, 0.35)',
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle id="alert-dialog-title" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', background: '#150a26', mb: 2, pb: 1.5 }}>
        {t('modals.deleteConfirmTitle')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {t('modals.deleteConfirmDesc')}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', pt: 1.5, pb: 2, px: 3 }}>
        <Button onClick={handleCloseDeletionDialog} color="inherit">
          {t('common.cancel')}
        </Button>
        <Button onClick={handleConfirmDeletion} color="primary" variant="contained" autoFocus>
          {t('common.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const assignedToDialog = (
    <Dialog
      open={assignedToDialogOpen}
      onClose={handleCloseAssignedToDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth={isIntegratedScreen ? false : 'xs'}
      fullWidth
      sx={isIntegratedScreen ? {
        '& .MuiDialog-container': {
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: { xs: '100%', md: '30%' },
          minWidth: { md: '320px' },
          maxWidth: { md: '30vw' },
          p: { xs: 1, sm: 1.5 },
          boxSizing: 'border-box',
          zIndex: 1300,
        },
        '& .MuiBackdrop-root': {
          width: { xs: '100%', md: '30%' },
          minWidth: { md: '320px' },
          maxWidth: { md: '30vw' },
          background: 'rgba(6, 1, 14, 0.85)',
          backdropFilter: 'blur(10px)',
        },
        '& .MuiDialog-paper': {
          width: '100% !important',
          maxWidth: '100% !important',
          m: '0 !important',
        }
      } : {}}
      PaperProps={{
        sx: {
          width: isIntegratedScreen ? '100%' : 'auto',
          maxWidth: isIntegratedScreen ? '100%' : '444px',
          background: '#0f081d !important',
          backgroundColor: '#0f081d !important',
          backdropFilter: 'none !important',
          border: '1px solid rgba(0, 240, 255, 0.35)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.9), 0 0 30px rgba(0, 240, 255, 0.2)',
          borderRadius: 3,
          m: isIntegratedScreen ? 0 : 'auto',
        }
      }}
    >
      <DialogTitle id="alert-dialog-title" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', background: '#150a26', mb: 1.5, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountCircle sx={{ color: '#00f2fe' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#ffffff' }}>
            {t('modals.assignSingerTitle')}
          </Typography>
        </Box>
        {existingParticipants.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Sparkles size={12} color="#00ff88" />
            <Typography sx={{ fontSize: '0.72rem', color: '#00ff88', fontWeight: 500 }}>
              {t('modals.detectedParticipants', { count: existingParticipants.length })}
            </Typography>
          </Box>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 2 }}>
        <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255, 255, 255, 0.65)', mb: 1.5 }}>
          {t('modals.assignSingerQuestion')}
        </Typography>

        <Autocomplete
          freeSolo
          autoHighlight
          options={existingParticipants}
          value={assignedToInputValue}
          onChange={(event, newValue) => {
            if (typeof newValue === 'string') {
              setAssignedToInputValue(newValue);
            } else if (newValue) {
              setAssignedToInputValue(newValue.toString());
            } else {
              setAssignedToInputValue('');
            }
          }}
          onInputChange={(event, newInputValue) => {
            setAssignedToInputValue(newInputValue);
          }}
          slotProps={{
            paper: {
              sx: {
                background: 'rgba(18, 8, 32, 0.98)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(0, 240, 255, 0.3)',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.7), 0 0 15px rgba(0, 240, 255, 0.2)',
                color: '#ffffff',
                my: 0.5,
                '& .MuiAutocomplete-noOptions': {
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.82rem',
                },
              }
            }
          }}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              key={option}
              sx={{
                py: 1,
                px: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.2,
                fontSize: '0.86rem',
                color: '#ffffff',
                transition: 'all 0.15s ease',
                '&:hover, &.Mui-focused': {
                  background: 'rgba(0, 240, 255, 0.15) !important',
                  color: '#00f2fe !important',
                }
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '7px',
                  background: 'rgba(0, 240, 255, 0.15)',
                  border: '1px solid rgba(0, 240, 255, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#00f2fe',
                  flexShrink: 0
                }}
              >
                <UserCheck size={13} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'inherit' }}>
                  {option}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255, 255, 255, 0.4)', textTransform: 'uppercase' }}>
                {t('modals.participant')}
              </Typography>
            </Box>
          )}
          renderInput={(params) => {
            const { InputProps, ...restParams } = params;
            const isNewParticipant = Boolean(
              assignedToInputValue.trim() &&
              !existingParticipants.some(p => p.toLowerCase() === assignedToInputValue.trim().toLowerCase())
            );

            return (
              <TextField
                {...restParams}
                fullWidth
                autoFocus
                size="medium"
                placeholder={t('modals.assignSingerPlaceholder')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleConfirmAssignedTo();
                  }
                }}
                inputProps={{
                  ...params.inputProps,
                  maxLength: 25,
                }}
                InputProps={{
                  ...InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <User size={18} color="#00f2fe" />
                      </InputAdornment>
                      {InputProps?.startAdornment}
                    </>
                  ),
                }}
                helperText={
                  isNewParticipant
                    ? `${t('modals.participant')}: "${assignedToInputValue.trim()}"`
                    : ''
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.04)',
                    color: '#ffffff',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.12)' },
                    '&:hover fieldset': { borderColor: 'rgba(0, 240, 255, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#00f2fe', borderWidth: 2 },
                  },
                  '& .MuiFormHelperText-root': {
                    color: isNewParticipant ? '#00ff88' : 'rgba(255, 255, 255, 0.5)',
                    fontSize: '0.75rem',
                    mx: 0.5,
                    mt: 0.8,
                    fontWeight: isNewParticipant ? 600 : 400
                  },
                }}
              />
            );
          }}
        />
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', pt: 1.5, pb: 2, px: 3 }}>
        <Button onClick={handleCloseAssignedToDialog} color="inherit" sx={{ textTransform: 'none', borderRadius: '10px' }}>
          {t('common.cancel')}
        </Button>
        <Button 
          onClick={() => handleConfirmAssignedTo()} 
          color="primary" 
          variant="contained" 
          sx={{ 
            textTransform: 'none', 
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #00d4ff 0%, #0072ff 100%)',
            fontWeight: 600
          }}
        >
          {t('modals.saveAssignment')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const currentStartTimeItem = playlistObj.find(it => it.id === startTimeItemId);

  const startTimeDialog = (
    <Dialog
      open={startTimeDialogOpen}
      onClose={handleCloseStartTimeDialog}
      aria-labelledby="start-time-dialog-title"
      aria-describedby="start-time-dialog-description"
      maxWidth={isIntegratedScreen ? false : 'xs'}
      fullWidth
      sx={isIntegratedScreen ? {
        position: 'fixed',
        left: 0,
        top: 0,
        width: { xs: '100%', md: '30%' },
        minWidth: { md: '320px' },
        maxWidth: { md: '30vw' },
        height: '100vh',
        zIndex: 1300,
        '& .MuiDialog-container': {
          width: '100%',
          height: '100%',
          p: 0,
          m: 0,
          boxSizing: 'border-box',
          zIndex: 1300,
        },
        '& .MuiBackdrop-root': {
          width: { xs: '100%', md: '30%' },
          minWidth: { md: '320px' },
          maxWidth: { md: '30vw' },
          background: 'rgba(6, 1, 14, 0.85)',
          backdropFilter: 'blur(10px)',
        },
        '& .MuiDialog-paper': {
          width: '100% !important',
          maxWidth: '100% !important',
          m: '0 !important',
        }
      } : {}}
      PaperProps={{
        sx: {
          width: isIntegratedScreen ? '100%' : 'auto',
          maxWidth: isIntegratedScreen ? '100%' : '460px',
          background: '#0f081d !important',
          backgroundColor: '#0f081d !important',
          backdropFilter: 'none !important',
          border: '1px solid rgba(0, 212, 255, 0.35)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.9), 0 0 30px rgba(0, 212, 255, 0.2)',
          borderRadius: 3,
          m: isIntegratedScreen ? 0 : 'auto',
        }
      }}
    >
      <DialogTitle id="start-time-dialog-title" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', background: '#150a26', mb: 1.5, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Clock size={20} color="#00d4ff" />
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#ffffff' }}>
            {t('modals.timeTrimTitle', 'Ajustar tiempos de inicio y fin')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1.5, fontSize: '0.82rem', lineHeight: 1.4 }}>
          {t('modals.timeTrimDesc', 'Configurá en qué segundo debe arrancar y terminar la pista en la pantalla y monitores.')}
        </Typography>

        {currentStartTimeItem?.title && (
          <Box sx={{ p: 1.2, mb: 2, background: 'rgba(255, 255, 255, 0.04)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <Typography sx={{ fontSize: '0.78rem', color: '#00d4ff', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              🎵 {currentStartTimeItem.title}
            </Typography>
          </Box>
        )}

        {/* Campo 1: Tiempo de Inicio */}
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#00d4ff', mb: 0.6 }}>
          1. {t('modals.startTimeTitle', 'Tiempo de inicio')}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder={t('modals.startTimePlaceholder', 'Ej: 15 o 1:20...')}
          value={startTimeInputValue}
          onChange={(e) => setStartTimeInputValue(e.target.value)}
          helperText={t('modals.startTimeHelper', 'Ingresá segundos o minutos:segundos (ej: 1:30 = 90s)')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Clock size={16} color="rgba(0, 212, 255, 0.7)" />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.2,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              color: '#ffffff',
              '& fieldset': { borderColor: 'rgba(0, 212, 255, 0.3)' },
              '&:hover fieldset': { borderColor: '#00d4ff' },
              '&.Mui-focused fieldset': { borderColor: '#00d4ff' }
            },
            '& .MuiFormHelperText-root': {
              color: 'rgba(255, 255, 255, 0.45)',
              fontSize: '0.7rem',
              mt: 0.3
            }
          }}
        />

        {/* Presets de inicio */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap', mb: 2.2 }}>
          {[
            { label: '0s (Inicio)', sec: 0 },
            { label: '10s', sec: 10 },
            { label: '15s', sec: 15 },
            { label: '30s', sec: 30 },
            { label: '45s', sec: 45 },
            { label: '1:00', sec: 60 },
          ].map((preset) => (
            <Button
              key={preset.label}
              size="small"
              onClick={() => {
                setStartTimeInputValue(preset.sec === 0 ? '' : formatSecondsToTime(preset.sec));
              }}
              sx={{
                textTransform: 'none',
                fontSize: '0.7rem',
                py: '1px',
                px: '7px',
                minWidth: 'auto',
                borderRadius: '6px',
                color: '#c7d2fe',
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  background: 'rgba(0, 212, 255, 0.15)',
                  borderColor: '#00d4ff',
                  color: '#00d4ff'
                }
              }}
            >
              {preset.label}
            </Button>
          ))}
        </Box>

        {/* Campo 2: Tiempo de Fin */}
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#ff77c2', mb: 0.6 }}>
          2. {t('modals.endTimeTitle', 'Tiempo de finalización (Corte)')}
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder={t('modals.endTimePlaceholder', 'Ej: 3:30 o 210s (vacío = fin de video)...')}
          value={endTimeInputValue}
          onChange={(e) => setEndTimeInputValue(e.target.value)}
          helperText={t('modals.endTimeHelper', 'Dejá vacío o en 0 para reproducir hasta el final original del video.')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Clock size={16} color="rgba(255, 119, 194, 0.7)" />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.2,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              color: '#ffffff',
              '& fieldset': { borderColor: 'rgba(255, 119, 194, 0.3)' },
              '&:hover fieldset': { borderColor: '#ff77c2' },
              '&.Mui-focused fieldset': { borderColor: '#ff77c2' }
            },
            '& .MuiFormHelperText-root': {
              color: 'rgba(255, 255, 255, 0.45)',
              fontSize: '0.7rem',
              mt: 0.3
            }
          }}
        />

        {/* Presets / atajos de fin si hay duración disponible */}
        {getTrackDuration(currentStartTimeItem) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap', mb: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.4)', width: '100%', mb: 0.2 }}>
              Atajos según duración ({formatSecondsToTime(getTrackDuration(currentStartTimeItem))}):
            </Typography>
            {[
              { label: 'Fin Completo', sec: 0 },
              { label: '-10s', sec: Math.max(0, getTrackDuration(currentStartTimeItem) - 10) },
              { label: '-15s', sec: Math.max(0, getTrackDuration(currentStartTimeItem) - 15) },
              { label: '-30s', sec: Math.max(0, getTrackDuration(currentStartTimeItem) - 30) },
            ].map((preset) => (
              <Button
                key={preset.label}
                size="small"
                onClick={() => {
                  setEndTimeInputValue(preset.sec === 0 ? '' : formatSecondsToTime(preset.sec));
                }}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.7rem',
                  py: '1px',
                  px: '7px',
                  minWidth: 'auto',
                  borderRadius: '6px',
                  color: '#fbcfe8',
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    background: 'rgba(255, 119, 194, 0.15)',
                    borderColor: '#ff77c2',
                    color: '#ff77c2'
                  }
                }}
              >
                {preset.label}
              </Button>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', pt: 1.5, pb: 2, px: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          {(currentStartTimeItem?.startTime > 0 || currentStartTimeItem?.t > 0 || currentStartTimeItem?.endTime > 0) && (
            <Button
              onClick={() => handleConfirmStartTime(0, 0)}
              sx={{ textTransform: 'none', borderRadius: '10px', color: '#ff4d6d', fontSize: '0.78rem' }}
            >
              {t('modals.resetToDefault', 'Restablecer a video completo')}
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleCloseStartTimeDialog} color="inherit" sx={{ textTransform: 'none', borderRadius: '10px' }}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => handleConfirmStartTime()}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #00d4ff 0%, #0072ff 100%)',
              fontWeight: 600
            }}
          >
            {t('modals.saveTiming', 'Guardar tiempos')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );

  const getColorItem = (item) => {
    const activeTargetId = activePlayingVideoId || videoId || currentPlaylistItem?.item?.videoId;
    if (activeTargetId && (activeTargetId === item.videoId || activeTargetId === item.id)) {
      return '#00FF11';
    } else if (item.played) {
      return '#888';
    } else {
      return '#fff';
    }
  };

  const clearDialog = (
    <Dialog
      open={clearDialogOpen}
      onClose={() => setClearDialogOpen(false)}
      aria-labelledby="clear-dialog-title"
      PaperProps={{
        sx: {
          background: '#0f081d !important',
          backgroundColor: '#0f081d !important',
          backdropFilter: 'none !important',
          border: '1px solid rgba(255, 45, 155, 0.35)',
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle id="clear-dialog-title" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', background: '#150a26', mb: 2, pb: 1.5 }}>
        {t('playlist.clearListTitle')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('playlist.clearListConfirm', { name: currentPlaylistDisplayName })}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', pt: 1.5, pb: 2, px: 3 }}>
        <Button onClick={() => setClearDialogOpen(false)} color="inherit">
          {t('common.cancel')}
        </Button>
        <Button onClick={handleConfirmClear} color="error" variant="contained" autoFocus>
          {t('playlist.clearListBtn')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const crossfadePopover = (
    <Popover
      open={Boolean(crossfadeAnchorEl)}
      anchorEl={crossfadeAnchorEl}
      onClose={() => setCrossfadeAnchorEl(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{
        paper: {
          sx: {
            background: 'rgba(15, 8, 29, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(0, 212, 255, 0.35)',
            borderRadius: '12px',
            p: 1.5,
            width: 190,
            boxShadow: '0 12px 36px rgba(0, 0, 0, 0.9), 0 0 20px rgba(0, 212, 255, 0.2)'
          }
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)' }}>
          {t('playlist.crossfadeDuration', 'Duración de fundido')}
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#00d4ff' }}>
          {currentCrossfadeSec}s
        </Typography>
      </Box>
      <Slider
        size="small"
        value={currentCrossfadeSec}
        min={2}
        max={12}
        step={1}
        marks={[
          { value: 2, label: '2s' },
          { value: 5, label: '5s' },
          { value: 12, label: '12s' }
        ]}
        onChange={(e, val) => {
          if (onUpdateCrossfadeDuration) onUpdateCrossfadeDuration(val);
          else handleLocalUpdateCrossfadeSec(val);
        }}
        sx={{
          color: '#00d4ff',
          '& .MuiSlider-thumb': {
            width: 14,
            height: 14,
            boxShadow: '0 0 8px #00d4ff',
          },
          '& .MuiSlider-track': {
            background: 'linear-gradient(90deg, #00d4ff 0%, #ff2d9b 100%)',
          },
          '& .MuiSlider-markLabel': {
            fontSize: '0.64rem',
            color: 'rgba(255, 255, 255, 0.45)'
          }
        }}
      />
    </Popover>
  );

  return (
    <>
      {deletionDialog}
      {assignedToDialog}
      {startTimeDialog}
      {clearDialog}
      {crossfadePopover}
      <Card elevation={3} style={{ padding: '10px 12px', margin: 0, height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        <CardContent style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
          {/* Titular en su propia línea independiente estilo ventana SO con opción de desmarcar y vaciar mediante iconos con tooltip */}
          <Box sx={{ mb: 0.8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="h6" component="div">
              {t('playlist.title')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              {/* Lupita sutil para abrir/cerrar buscador dinámico */}
              {playlistObj && playlistObj.length > 0 && (
                <Tooltip
                  title={isSearchOpen ? (t('playlist.closeSearch') || 'Cerrar buscador') : (t('playlist.search') || 'Buscar en la lista')}
                  arrow
                  placement="top"
                >
                  <IconButton
                    size="small"
                    id="toggle-playlist-search-btn"
                    onClick={() => {
                      setIsSearchOpen(prev => {
                        const next = !prev;
                        if (!next) setSearchQuery('');
                        else setTimeout(() => searchInputRef.current?.focus(), 80);
                        return next;
                      });
                    }}
                    sx={{
                      p: '5px',
                      color: (isSearchOpen || searchQuery) ? '#00ff88' : 'rgba(255, 255, 255, 0.45)',
                      background: (isSearchOpen || searchQuery) ? 'rgba(0, 255, 136, 0.12)' : 'transparent',
                      border: (isSearchOpen || searchQuery) ? '1px solid rgba(0, 255, 136, 0.4)' : '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: '#00ff88',
                        background: 'rgba(0, 255, 136, 0.22)',
                        borderColor: '#00ff88',
                        boxShadow: '0 0 10px rgba(0, 255, 136, 0.35)',
                      }
                    }}
                  >
                    <Search size={16} />
                  </IconButton>
                </Tooltip>
              )}
              {playlistObj && playlistObj.length > 0 && (
                <Tooltip
                  title={playedCount > 0
                    ? `${t('playlist.uncheckAllPlayedTooltip', 'Desmarcar todas las canciones tachadas')} (${playedCount})`
                    : t('playlist.noPlayedToUncheck', 'No hay canciones tachadas en esta lista')
                  }
                  arrow
                  placement="top"
                >
                  <span>
                    <IconButton
                      size="small"
                      id="uncheck-all-played-btn"
                      disabled={playedCount === 0}
                      onClick={handleUncheckAllPlayed}
                      sx={{
                        p: '5px',
                        color: playedCount > 0 ? '#00d4ff' : 'rgba(255, 255, 255, 0.25)',
                        background: playedCount > 0 ? 'rgba(0, 212, 255, 0.08)' : 'transparent',
                        border: playedCount > 0 ? '1px solid rgba(0, 212, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.08)',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          color: '#ffffff',
                          background: 'rgba(0, 212, 255, 0.22)',
                          borderColor: '#00d4ff',
                          boxShadow: '0 0 10px rgba(0, 212, 255, 0.4)',
                        },
                        '&.Mui-disabled': {
                          color: 'rgba(255, 255, 255, 0.2)',
                          borderColor: 'rgba(255, 255, 255, 0.06)',
                        }
                      }}
                    >
                      <RotateCcw size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {isCurrentMyPlaylist && playlistObj && playlistObj.length > 0 && (
                <Tooltip title={`${t('playlist.clearList')} (${playlistObj.length})`} arrow placement="top">
                  <IconButton
                    size="small"
                    id="clear-playlist-btn"
                    onClick={() => setClearDialogOpen(true)}
                    sx={{
                      p: '5px',
                      color: 'rgba(255, 77, 109, 0.9)',
                      background: 'rgba(255, 77, 109, 0.08)',
                      border: '1px solid rgba(255, 77, 109, 0.25)',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: '#ffffff',
                        background: 'rgba(255, 77, 109, 0.22)',
                        borderColor: '#ff4d6d',
                        boxShadow: '0 0 10px rgba(255, 77, 109, 0.4)',
                      }
                    }}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Barra de búsqueda dinámica sutil y expandible */}
          {isSearchOpen && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                mb: 0.8,
                px: 1,
                py: 0.35,
                borderRadius: '8px',
                background: 'rgba(0, 0, 0, 0.45)',
                border: '1px solid rgba(0, 255, 136, 0.35)',
                boxShadow: '0 0 10px rgba(0, 255, 136, 0.12)',
                flexShrink: 0,
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              <Search size={14} color="#00ff88" style={{ flexShrink: 0 }} />
              <InputBase
                inputRef={searchInputRef}
                autoFocus
                placeholder={t('playlist.searchPlaceholder') || 'Buscar por tema o cantante...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setSearchQuery('');
                    setIsSearchOpen(false);
                  }
                }}
                sx={{
                  flex: 1,
                  fontSize: '0.8rem',
                  color: '#ffffff',
                  '& input': {
                    p: '3px 0',
                    '&::placeholder': { color: 'rgba(255, 255, 255, 0.45)', opacity: 1 }
                  }
                }}
              />
              {searchQuery && (
                <Typography sx={{ fontSize: '0.7rem', color: '#00ff88', fontWeight: 600, flexShrink: 0, mr: 0.5 }}>
                  {filteredPlaylist.length} / {playlistObj.length}
                </Typography>
              )}
              <Tooltip title={t('playlist.clearSearch') || 'Limpiar'} arrow placement="top">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (searchQuery) setSearchQuery('');
                    else setIsSearchOpen(false);
                  }}
                  sx={{
                    p: '2px',
                    color: 'rgba(255, 255, 255, 0.45)',
                    '&:hover': { color: '#ff4d6d' }
                  }}
                >
                  <X size={14} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* Fila de controles: Select de ancho constante máximo + botones de exportar/importar/gestionar al final */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1, flexShrink: 0, width: '100%' }}>
            <Select
              labelId="select-playlist"
              id="select-playlist"
              value={safeSelectValue}
              onChange={handleChangePlaylist}
              size="small"
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: '0.82rem',
                borderRadius: '10px',
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '& .MuiSelect-select': {
                  py: 0.7,
                  px: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 380,
                    background: 'rgba(18, 8, 32, 0.98)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(0, 212, 255, 0.2)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
                  }
                }
              }}
            >
              {safeSelectValue && !allAvailableValues.includes(safeSelectValue) && (
                <MenuItem key={safeSelectValue} value={safeSelectValue} sx={{ display: 'none' }}>
                  {safeSelectValue}
                </MenuItem>
              )}
              <ListSubheader sx={{
                background: '#120820',
                color: '#ff2d9b',
                fontSize: '0.72rem',
                fontWeight: 800,
                letterSpacing: '0.8px',
                textTransform: 'uppercase',
                lineHeight: '28px'
              }}>
                {t('playlist.myLists')} ({myPlaylists.length})
              </ListSubheader>
              {myPlaylists.map((pl) => (
                <MenuItem
                  key={pl.id}
                  value={pl.id}
                  sx={{
                    fontSize: '0.82rem',
                    color: currentPlaylist === pl.id ? '#00ff88' : 'rgba(255, 255, 255, 0.85)',
                    '&:hover': { background: 'rgba(255, 45, 155, 0.12)' },
                    display: 'flex',
                    justifyContent: 'space-between'
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pl.name}</span>
                  <span style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.35)', marginLeft: 8 }}>
                    {pl.items ? pl.items.length : 0}
                  </span>
                </MenuItem>
              ))}

              <ListSubheader sx={{
                background: '#120820',
                color: '#00d4ff',
                fontSize: '0.72rem',
                fontWeight: 800,
                letterSpacing: '0.8px',
                textTransform: 'uppercase',
                lineHeight: '28px',
                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                mt: 0.5
              }}>
                {t('playlist.systemLists')}
              </ListSubheader>
              {originPresetPlaylists.map((originPlaylist) => (
                <MenuItem
                  key={originPlaylist.name}
                  value={originPlaylist.name}
                  sx={{
                    fontSize: '0.82rem',
                    color: currentPlaylist === originPlaylist.name ? '#00d4ff' : 'rgba(255, 255, 255, 0.85)',
                    '&:hover': { background: 'rgba(0, 212, 255, 0.12)' }
                  }}
                >
                  {originPlaylist.name}
                </MenuItem>
              ))}
            </Select>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              {/* Exportar lista */}
              <Tooltip title={t('playlist.export')}>
                <IconButton
                  size="small"
                  id="export-playlist-btn"
                  onClick={handleExport}
                  sx={{
                    p: '6px',
                    color: '#00d4ff',
                    background: 'rgba(0, 212, 255, 0.08)',
                    border: '1px solid rgba(0, 212, 255, 0.2)',
                    borderRadius: '8px',
                    '&:hover': { background: 'rgba(0, 212, 255, 0.2)', borderColor: '#00d4ff' }
                  }}
                >
                  <GetAppIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Importar lista */}
              <Tooltip title={t('playlist.import')}>
                <IconButton
                  size="small"
                  id="import-playlist-btn"
                  onClick={handleImportFileSelect}
                  sx={{
                    p: '6px',
                    color: '#ff2d9b',
                    background: 'rgba(255, 45, 155, 0.08)',
                    border: '1px solid rgba(255, 45, 155, 0.2)',
                    borderRadius: '8px',
                    '&:hover': { background: 'rgba(255, 45, 155, 0.2)', borderColor: '#ff2d9b' }
                  }}
                >
                  <FileUploadIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Gestionar mis listas */}
              <Tooltip title={t('playlist.manageLists')}>
                <IconButton
                  size="small"
                  id="manage-playlists-btn"
                  onClick={() => setPlaylistManagerOpen(true)}
                  sx={{
                    p: '6px',
                    color: '#00ff88',
                    background: 'rgba(0, 255, 136, 0.08)',
                    border: '1px solid rgba(0, 255, 136, 0.2)',
                    borderRadius: '8px',
                    '&:hover': { background: 'rgba(0, 255, 136, 0.2)', borderColor: '#00ff88' }
                  }}
                >
                  <FolderKanban size={17} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Fila de Automatización: Reproducción Continua y Crossfade */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.6,
              mb: 0.9,
              px: 1,
              py: 0.45,
              borderRadius: '10px',
              background: 'linear-gradient(90deg, rgba(255, 45, 155, 0.05) 0%, rgba(0, 212, 255, 0.05) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              flexShrink: 0,
              flexWrap: 'nowrap',
              overflow: 'hidden'
            }}
          >
            {/* Control 1: Reproducción Continua (Autoplay) */}
            <Tooltip title={t('playlist.continuousPlayTooltip', 'Avanza automáticamente al siguiente tema al terminar la canción')} arrow placement="top">
              <Box
                onClick={() => onToggleContinuousPlay ? onToggleContinuousPlay(!isContinuous) : handleLocalToggleContinuous()}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  userSelect: 'none',
                  py: 0.2,
                  px: 0.5,
                  borderRadius: '6px',
                  transition: 'background 0.2s',
                  '&:hover': { background: 'rgba(0, 255, 136, 0.08)' }
                }}
              >
                <Repeat size={14} color={isContinuous ? '#00ff88' : 'rgba(255, 255, 255, 0.4)'} />
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: isContinuous ? 700 : 500,
                    color: isContinuous ? '#00ff88' : 'rgba(255, 255, 255, 0.65)',
                    letterSpacing: '0.01em',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {t('playlist.continuousPlay', 'Continua')}
                </Typography>
                <Switch
                  size="small"
                  checked={isContinuous}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (onToggleContinuousPlay) onToggleContinuousPlay(e.target.checked);
                    else handleLocalToggleContinuous(e.target.checked);
                  }}
                  sx={{
                    width: 30,
                    height: 18,
                    p: 0,
                    ml: 0.2,
                    '& .MuiSwitch-switchBase': {
                      p: 0,
                      m: '2px',
                      transitionDuration: '200ms',
                      '&.Mui-checked': {
                        transform: 'translateX(12px)',
                        color: '#fff',
                        '& + .MuiSwitch-track': {
                          backgroundColor: '#00ff88',
                          opacity: 0.85,
                          border: 0,
                        },
                      },
                    },
                    '& .MuiSwitch-thumb': {
                      width: 14,
                      height: 14,
                    },
                    '& .MuiSwitch-track': {
                      borderRadius: 9,
                      backgroundColor: 'rgba(255, 255, 255, 0.16)',
                      opacity: 1,
                    },
                  }}
                />
              </Box>
            </Tooltip>

            {/* Separador vertical sutil */}
            <Box sx={{ width: '1px', height: 16, background: 'rgba(255, 255, 255, 0.1)', flexShrink: 0 }} />

            {/* Control 2: Crossfade (Fundido Cruzado) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title={t('playlist.crossfadeTooltip', 'Fundido cruzado gradual entre canciones')} arrow placement="top">
                <Box
                  onClick={() => onToggleCrossfade ? onToggleCrossfade(!isCrossfade) : handleLocalToggleCrossfade()}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    userSelect: 'none',
                    py: 0.2,
                    px: 0.5,
                    borderRadius: '6px',
                    transition: 'background 0.2s',
                    '&:hover': { background: 'rgba(0, 212, 255, 0.08)' }
                  }}
                >
                  <SlidersHorizontal size={13} color={isCrossfade ? '#00d4ff' : 'rgba(255, 255, 255, 0.4)'} />
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: isCrossfade ? 700 : 500,
                      color: isCrossfade ? '#00d4ff' : 'rgba(255, 255, 255, 0.65)',
                      letterSpacing: '0.01em',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {t('playlist.crossfade', 'Crossfade')}
                  </Typography>
                  <Switch
                    size="small"
                    checked={isCrossfade}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (onToggleCrossfade) onToggleCrossfade(e.target.checked);
                      else handleLocalToggleCrossfade(e.target.checked);
                    }}
                    sx={{
                      width: 30,
                      height: 18,
                      p: 0,
                      ml: 0.2,
                      '& .MuiSwitch-switchBase': {
                        p: 0,
                        m: '2px',
                        transitionDuration: '200ms',
                        '&.Mui-checked': {
                          transform: 'translateX(12px)',
                          color: '#fff',
                          '& + .MuiSwitch-track': {
                            backgroundColor: '#00d4ff',
                            opacity: 0.85,
                            border: 0,
                          },
                        },
                      },
                      '& .MuiSwitch-thumb': {
                        width: 14,
                        height: 14,
                      },
                      '& .MuiSwitch-track': {
                        borderRadius: 9,
                        backgroundColor: 'rgba(255, 255, 255, 0.16)',
                        opacity: 1,
                      },
                    }}
                  />
                </Box>
              </Tooltip>

              {/* Botón píldora para configurar los segundos de Crossfade */}
              {isCrossfade && (
                <Tooltip title={t('playlist.crossfadeDuration', 'Ajustar duración del fundido')} arrow placement="top">
                  <Box
                    component="button"
                    type="button"
                    onClick={(e) => setCrossfadeAnchorEl(e.currentTarget)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 20,
                      px: 0.9,
                      borderRadius: '10px',
                      fontSize: '0.68rem',
                      fontFamily: 'inherit',
                      fontWeight: 700,
                      cursor: 'pointer',
                      background: 'rgba(0, 212, 255, 0.15)',
                      color: '#00d4ff',
                      border: '1px solid rgba(0, 212, 255, 0.35)',
                      boxShadow: '0 0 8px rgba(0, 212, 255, 0.2)',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'rgba(0, 212, 255, 0.25)',
                        borderColor: '#00d4ff',
                        transform: 'scale(1.04)'
                      }
                    }}
                  >
                    {currentCrossfadeSec}s
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Box>

          <div id="playList" className="playlist_container scroll">
            <DragDropContext onDragEnd={handleDragEndPlaylist}>
              <StrictModeDroppable droppableId="playlist-droppable" direction="vertical">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {filteredPlaylist.length === 0 && searchQuery ? (
                      <Box sx={{ py: 3, px: 2, textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)' }}>
                        <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255, 255, 255, 0.75)', mb: 1.2 }}>
                          {t('playlist.noSearchResults', { query: searchQuery }) || `No se encontraron canciones para "${searchQuery}"`}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setSearchQuery('')}
                          sx={{
                            fontSize: '0.72rem',
                            color: '#00ff88',
                            borderColor: 'rgba(0, 255, 136, 0.35)',
                            textTransform: 'none',
                            py: 0.3,
                            px: 1.5,
                            borderRadius: '6px',
                            '&:hover': {
                              borderColor: '#00ff88',
                              background: 'rgba(0, 255, 136, 0.1)'
                            }
                          }}
                        >
                          {t('playlist.clearSearch') || 'Limpiar búsqueda'}
                        </Button>
                      </Box>
                    ) : (
                      filteredPlaylist.map((item, index) => {
                        const activeTargetId = activePlayingVideoId || videoId || currentPlaylistItem?.item?.videoId;
                        const isPlayingThis = Boolean(
                          activeTargetId && (
                            item.videoId === activeTargetId ||
                            item.id === activeTargetId ||
                            String(item.id) === String(activeTargetId)
                          )
                        );
                        const trackDur = getTrackDuration(item);
                        const dragId = String(item.id || item.videoId || `item-${index}`);
                        return (
                          <Draggable key={dragId} draggableId={dragId} index={index} isDragDisabled={Boolean(searchQuery.trim())}>
                          {(provided, snapshot) => {
                            const child = (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  marginBottom: '3px',
                                  opacity: snapshot.isDragging ? 0.95 : 1,
                                  zIndex: snapshot.isDragging ? 99999 : 'auto',
                                }}
                              >
                                <Box
                                  sx={{
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: '10px',
                                    background: isPlayingThis
                                      ? 'linear-gradient(90deg, rgba(0, 255, 136, 0.12) 0%, rgba(18, 8, 32, 0.95) 100%)'
                                      : snapshot.isDragging
                                        ? 'rgba(255, 12, 44, 0.98)'
                                        : 'rgba(255, 255, 255, 0.03)',
                                    border: isPlayingThis
                                      ? '1px solid rgba(0, 255, 136, 0.45)'
                                      : snapshot.isDragging
                                        ? '1px solid #ff2d9b'
                                        : '1px solid rgba(255, 255, 255, 0.05)',
                                    boxShadow: isPlayingThis ? '0 0 12px rgba(0, 255, 136, 0.18)' : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.8,
                                    transition: 'all 0.15s ease',
                                    '&:hover': {
                                      background: isPlayingThis ? 'rgba(0, 255, 136, 0.16)' : 'rgba(255, 255, 255, 0.05)',
                                      borderColor: isPlayingThis ? '#00ff88' : 'rgba(255, 255, 255, 0.12)'
                                    }
                                  }}
                                >
                                  {/* Grip de arrastre con dragHandleProps explícito y feedback neón */}
                                  <Box
                                    {...provided.dragHandleProps}
                                    sx={{
                                      color: snapshot.isDragging ? '#00ff88' : 'rgba(255, 255, 255, 0.25)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      cursor: 'grab',
                                      p: '3px',
                                      borderRadius: '4px',
                                      touchAction: 'none',
                                      userSelect: 'none',
                                      transition: 'color 0.15s ease, background 0.15s ease',
                                      '&:hover': { color: '#00ff88', background: 'rgba(0, 255, 136, 0.12)' },
                                      '&:active': { cursor: 'grabbing' }
                                    }}
                                    title={t('playlist.dragToReorder')}
                                  >
                                    <GripVertical size={13} />
                                  </Box>

                                  {/* Checkbox de cantado/completado */}
                                  <Checkbox
                                    size="small"
                                    checked={item.played}
                                    onClick={(e) => handleCheckVideo(e.target.checked, item.id)}
                                    sx={{
                                      p: '2px',
                                      color: 'rgba(255, 255, 255, 0.25)',
                                      '&.Mui-checked': { color: '#ff2d9b' }
                                    }}
                                  />

                                  {/* Miniatura 16:9 con indicador de turno y efecto animado de Zoom In */}
                                  <ThumbnailZoom
                                    src={item.thumbnail}
                                    alt={item.title}
                                    title={item.title}
                                    badge={`#${index + 1}`}
                                    placement="right"
                                  />

                                  {/* Avatar de contacto / cantante asignado */}
                                  <Box
                                    onClick={() => handleOpenEditAssignedToDialog(item.id)}
                                    sx={{
                                      width: 26,
                                      height: 26,
                                      flexShrink: 0,
                                      borderRadius: '50%',
                                      background: item.assignedTo ? 'linear-gradient(135deg, #ff2d9b 0%, #b44dff 100%)' : 'rgba(255, 255, 255, 0.06)',
                                      border: item.assignedTo ? '1px solid #ff2d9b' : '1px dashed rgba(255, 255, 255, 0.2)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer',
                                      color: '#ffffff',
                                      fontSize: '0.72rem',
                                      fontWeight: 700,
                                      transition: 'all 0.2s ease',
                                      '&:hover': {
                                        transform: 'scale(1.08)',
                                        borderColor: '#ff2d9b'
                                      }
                                    }}
                                    title={item.assignedTo ? `${t('playlist.singer')}: ${item.assignedTo}` : t('playlist.assignSinger')}
                                  >
                                    {item.assignedTo ? item.assignedTo.charAt(0).toUpperCase() : <User size={12} color="rgba(255, 255, 255, 0.5)" />}
                                  </Box>

                                  {/* Info principal: Título y detalle de cantante estilo lista de contactos con Tooltip */}
                                  <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
                                    <Tooltip title={item.title} arrow placement="top-start">
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          textDecoration: item.played ? 'line-through' : 'none',
                                          color: isPlayingThis ? '#00ff88' : item.played ? 'rgba(255, 255, 255, 0.4)' : '#ffffff',
                                          fontSize: '0.78rem',
                                          fontWeight: isPlayingThis ? 700 : 500,
                                          lineHeight: 1.15,
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis'
                                        }}
                                      >
                                        {item.title}
                                      </Typography>
                                    </Tooltip>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.15, flexWrap: 'wrap' }}>
                                      {item.assignedTo ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                          <Typography sx={{ fontSize: '0.67rem', color: '#ff77c2', fontWeight: 600, lineHeight: 1 }}>
                                            {item.assignedTo}
                                          </Typography>
                                          <span
                                            onClick={() => handleDeleteAssignedTo(item.id)}
                                            style={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.35)', cursor: 'pointer', marginLeft: 2 }}
                                            title={t('playlist.removeSinger')}
                                          >
                                            ×
                                          </span>
                                        </Box>
                                      ) : (
                                        <Typography
                                          onClick={() => handleOpenEditAssignedToDialog(item.id)}
                                          sx={{
                                            fontSize: '0.66rem',
                                            color: 'rgba(255, 255, 255, 0.35)',
                                            cursor: 'pointer',
                                            '&:hover': { color: 'rgba(255, 255, 255, 0.7)' }
                                          }}
                                        >
                                          {t('playlist.unassigned')}
                                        </Typography>
                                      )}
                                      {trackDur !== null && (
                                        <>
                                          <Typography 
                                            component="span" 
                                            sx={{ 
                                              fontSize: '0.62rem', 
                                              color: 'rgba(255, 255, 255, 0.22)', 
                                              lineHeight: 1, 
                                              userSelect: 'none' 
                                            }}
                                          >
                                            •
                                          </Typography>
                                          <Typography 
                                            component="span"
                                            sx={{ 
                                              fontSize: '0.67rem', 
                                              color: 'rgba(255, 255, 255, 0.45)', 
                                              fontWeight: 600,
                                              lineHeight: 1,
                                              background: 'rgba(255, 255, 255, 0.05)',
                                              px: 0.5,
                                              py: 0.1,
                                              borderRadius: '3px'
                                            }}
                                          >
                                            {formatSecondsToTime(trackDur)}
                                          </Typography>
                                        </>
                                      )}

                                      {/* Botón / Badge de tiempo individual de inicio y fin (t) en la segunda línea */}
                                      {(() => {
                                        const startSec = item.startTime || item.t || 0;
                                        const endSec = item.endTime || 0;
                                        const hasStart = startSec > 0;
                                        const hasEnd = endSec > 0;
                                        const hasTiming = hasStart || hasEnd;

                                        let tooltipLabel = t('playlist.editStartTime', 'Ajustar tiempos de inicio y fin');
                                        let badgeLabel = '';
                                        if (hasStart && hasEnd) {
                                          tooltipLabel = `Inicio: ${formatSecondsToTime(startSec)} · Fin: ${formatSecondsToTime(endSec)}`;
                                          badgeLabel = `${formatSecondsToTime(startSec)} - ${formatSecondsToTime(endSec)}`;
                                        } else if (hasStart) {
                                          tooltipLabel = `Inicio: ${formatSecondsToTime(startSec)}`;
                                          badgeLabel = formatSecondsToTime(startSec);
                                        } else if (hasEnd) {
                                          tooltipLabel = `Fin (Corte): ${formatSecondsToTime(endSec)}`;
                                          badgeLabel = `Fin: ${formatSecondsToTime(endSec)}`;
                                        }

                                        return (
                                          <>
                                            <Typography 
                                              component="span" 
                                              sx={{ 
                                                fontSize: '0.62rem', 
                                                color: 'rgba(255, 255, 255, 0.22)', 
                                                lineHeight: 1, 
                                                userSelect: 'none' 
                                              }}
                                            >
                                              •
                                            </Typography>
                                            <Tooltip title={`${tooltipLabel} · ${t('playlist.editStartTime', 'Editar tiempos')}`}>
                                              <Box
                                                onClick={() => handleOpenEditStartTimeDialog(item.id)}
                                                sx={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: 0.35,
                                                  cursor: 'pointer',
                                                  px: hasTiming ? '5px' : '3px',
                                                  py: '1.5px',
                                                  borderRadius: '4px',
                                                  fontSize: '0.65rem',
                                                  fontWeight: 700,
                                                  color: hasTiming ? '#00d4ff' : 'rgba(255, 255, 255, 0.35)',
                                                  background: hasTiming ? 'rgba(0, 212, 255, 0.12)' : 'transparent',
                                                  border: '1px solid',
                                                  borderColor: hasTiming ? 'rgba(0, 212, 255, 0.35)' : 'transparent',
                                                  transition: 'all 0.15s ease',
                                                  '&:hover': {
                                                    color: '#00d4ff',
                                                    background: 'rgba(0, 212, 255, 0.18)',
                                                    borderColor: '#00d4ff',
                                                    transform: 'scale(1.04)'
                                                  }
                                                }}
                                              >
                                                <Clock size={11} />
                                                {hasTiming && (
                                                  <span>{badgeLabel}</span>
                                                )}
                                              </Box>
                                            </Tooltip>
                                          </>
                                        );
                                      })()}
                                    </Box>
                                  </Box>

                                  {/* Acciones flat pro a la derecha: Exclusivamente Eliminar y Play */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                    {isCurrentMyPlaylist && (
                                      <Tooltip title={t('playlist.removeFromList')}>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleOpenDeletionDialog(item.id)}
                                          sx={{
                                            p: '4px',
                                            color: 'rgba(255, 255, 255, 0.3)',
                                            borderRadius: '6px',
                                            '&:hover': { color: '#ff4d6d', background: 'rgba(255, 77, 109, 0.12)' }
                                          }}
                                        >
                                          <Trash2 size={14} />
                                        </IconButton>
                                      </Tooltip>
                                    )}

                                    {/* Botón Play con indicador de avance circular */}
                                    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                      {isPlayingThis && (
                                        <svg
                                          style={{
                                            position: 'absolute',
                                            top: -3,
                                            left: -3,
                                            width: 30,
                                            height: 30,
                                            pointerEvents: 'none',
                                            transform: 'rotate(-90deg)',
                                            zIndex: 1,
                                          }}
                                          viewBox="0 0 32 32"
                                        >
                                          <circle
                                            cx="16"
                                            cy="16"
                                            r="13.5"
                                            fill="none"
                                            stroke="rgba(0, 255, 136, 0.18)"
                                            strokeWidth="2.2"
                                          />
                                          <circle
                                            cx="16"
                                            cy="16"
                                            r="13.5"
                                            fill="none"
                                            stroke="#00ff88"
                                            strokeWidth="2.2"
                                            strokeDasharray={84.82}
                                            strokeDashoffset={84.82 * (1 - (playbackProgress[item.videoId || item.id] || 0))}
                                            strokeLinecap="round"
                                            style={{
                                              transition: 'stroke-dashoffset 0.35s linear',
                                              filter: 'drop-shadow(0 0 4px rgba(0, 255, 136, 0.7))',
                                            }}
                                          />
                                        </svg>
                                      )}
                                      <Tooltip title={t('playlist.playNow')}>
                                        <IconButton
                                          size="small"
                                          onClick={() => playNow(item)}
                                          sx={{
                                            p: '4px',
                                            borderRadius: '6px',
                                            color: isPlayingThis ? '#00ff88' : 'rgba(255, 255, 255, 0.7)',
                                            background: isPlayingThis ? 'rgba(0, 255, 136, 0.12)' : 'transparent',
                                            boxShadow: isPlayingThis ? '0 0 8px rgba(0, 255, 136, 0.25)' : 'none',
                                            '&:hover': { color: '#00ff88', background: 'rgba(0, 255, 136, 0.15)' }
                                          }}
                                        >
                                          <Play size={15} fill={isPlayingThis ? '#00ff88' : 'currentColor'} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                </Box>
                              </div>
                            );

                            if (snapshot.isDragging) {
                              return ReactDOM.createPortal(child, document.body);
                            }

                            return child;
                          }}
                        </Draggable>
                      );
                    }))}
                    {provided.placeholder}
                  </div>
                )}
              </StrictModeDroppable>
            </DragDropContext>
          </div>
        </CardContent>
      </Card>

      {/* Modal ABM de gestión de listas propias */}
      <PlaylistManagerDialog
        open={playlistManagerOpen}
        onClose={() => setPlaylistManagerOpen(false)}
        myPlaylists={myPlaylists}
        currentPlaylistId={currentPlaylist}
        onSelectPlaylist={(plId) => {
          setCurrentPlaylist(plId);
          const targetPl = myPlaylists.find(p => p.id === plId);
          if (targetPl) {
            setPlaylist(targetPl.items || []);
            setPlaylistObj(targetPl.items || []);
          }
        }}
        onCreatePlaylist={onCreatePlaylist}
        onRenamePlaylist={onRenamePlaylist}
        onDeletePlaylist={onDeletePlaylist}
        originPresetPlaylists={originPresetPlaylists}
      />

      {/* Modal interactivo de opciones al importar lista */}
      <ImportPlaylistDialog
        open={importDialogOpen}
        onClose={() => {
          setImportDialogOpen(false);
          setPendingImportData(null);
        }}
        importData={pendingImportData}
        currentPlaylistName={currentPlaylistDisplayName}
        onOverwriteCurrent={handleConfirmImportOverwrite}
        onOverwrite={handleConfirmImportOverwrite}
        onAppendCurrent={handleConfirmImportAppend}
        onAppend={handleConfirmImportAppend}
        onCreateNewPlaylist={handleConfirmImportCreateNew}
        onCreateNew={handleConfirmImportCreateNew}
        existingPlaylistNames={myPlaylists ? myPlaylists.map(p => p.name) : []}
      />
    </>
  );
};

export default memo(PlayListCard);
